import React, {createContext, useContext, useState, useEffect, useCallback} from 'react';
import { AUTH_URL } from '../config/api';
import { AUTH_UNAUTHORIZED_EVENT } from '../utils/apiClient';
import { useIdleTimer } from '../hooks/useIdleTimer';

interface User {
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (credentials: any) => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const INACTIVITY_LIMIT = 30 * 60 * 1000;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const isAuthenticated = !!user;

    const logout = useCallback(() => {
        console.log("Cerrando sesión...");
        setUser(null);
        localStorage.removeItem('st_user');
        window.location.href = '/';
    }, []);

    useIdleTimer(
        logout,
        INACTIVITY_LIMIT,
        isAuthenticated
    );

    useEffect(() => {
        const handleUnauthorized = () => {
            logout();
        };

        window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);

        return () => {
            window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
        };
    }, []);

    useEffect(() => {
        const storedUser = localStorage.getItem('st_user');
        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
            } catch (e) {
                console.error("Error parsing user");
                localStorage.removeItem('st_user');
            }
        }
        setIsLoading(false);
    }, []);

    const login = async (credentials: any) => {
        if (!credentials || !credentials.password) return;

        setIsLoading(true);
        try {
            const response = await fetch(`${AUTH_URL}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials),
                credentials: 'include',
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Login failed: ${errorText}`);
            }

            const userData: User = await response.json();
            setUser(userData);
            localStorage.setItem('st_user', JSON.stringify(userData));

        } catch (error) {
            console.error(error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthContext.Provider value={{ user, isAuthenticated: !!user, isLoading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};